import { create } from "zustand";
import { persist } from "zustand/middleware";
import { LAYER_REGISTRY } from "../map/registry";
import { resolveLevel, type LevelSetting, type PriceLevel } from "../map/levels";
import type { TransitView } from "../map/layers/transit";

interface LayerState {
  /** layer id -> visible. Absent means "use the registry default". */
  visibility: Record<string, boolean>;
  /** Which year the price layers display. */
  year: number;
  /** Layers whose tiles are missing — usually a pipeline that has not been run. */
  unavailable: string[];
  setUnavailable: (ids: string[]) => void;
  /** "auto" follows the zoom; anything else pins that geography. */
  levelSetting: LevelSetting;
  setLevelSetting: (level: LevelSetting) => void;
  /** Current map zoom, mirrored here so the level can be derived from it. */
  zoom: number;
  setZoom: (zoom: number) => void;
  /** The geography actually in force right now. */
  activeLevel: () => PriceLevel;
  /** Whether the transit layer draws stops or the lines themselves. */
  transitView: TransitView;
  setTransitView: (view: TransitView) => void;
  /**
   * Lines the user has picked out of the legend. Empty means "no focus", which
   * is not the same as "all selected" — with a focus, everything else dims.
   */
  highlightedLines: string[];
  toggleLine: (line: string) => void;
  /** Add a whole mode's lines to the focus, or drop them if all are already in. */
  toggleLines: (lines: string[]) => void;
  clearLines: () => void;
  toggle: (id: string) => void;
  setVisible: (id: string, visible: boolean) => void;
  setYear: (year: number) => void;
  isVisible: (id: string) => boolean;
}

export const LATEST_YEAR = 2025;

export const useLayerStore = create<LayerState>()(
  persist(
    (set, get) => ({
      visibility: Object.fromEntries(
        LAYER_REGISTRY.map((l) => [l.id, l.defaultVisible]),
      ),
      year: LATEST_YEAR,
      unavailable: [],
      setUnavailable: (ids) => set({ unavailable: ids }),
      levelSetting: "auto",
      setLevelSetting: (levelSetting) => set({ levelSetting }),
      zoom: 10.2,
      setZoom: (zoom) => set({ zoom }),
      activeLevel: () => resolveLevel(get().levelSetting, get().zoom),
      transitView: "stops",
      setTransitView: (transitView) => set({ transitView }),
      highlightedLines: [],
      toggleLine: (line) =>
        set((s) => ({
          highlightedLines: s.highlightedLines.includes(line)
            ? s.highlightedLines.filter((l) => l !== line)
            : [...s.highlightedLines, line],
        })),
      toggleLines: (lines) =>
        set((s) => {
          // Adding a mode leaves whatever else is focused alone — the whole
          // point is building up a comparison across modes. Only when the mode
          // is already fully in does a second click take it back out.
          const already = lines.every((l) => s.highlightedLines.includes(l));
          return {
            highlightedLines: already
              ? s.highlightedLines.filter((l) => !lines.includes(l))
              : [
                  ...s.highlightedLines,
                  ...lines.filter((l) => !s.highlightedLines.includes(l)),
                ],
          };
        }),
      clearLines: () => set({ highlightedLines: [] }),
      toggle: (id) =>
        set((s) => ({ visibility: { ...s.visibility, [id]: !s.isVisible(id) } })),
      setVisible: (id, visible) =>
        set((s) => ({ visibility: { ...s.visibility, [id]: visible } })),
      setYear: (year) => set({ year }),
      isVisible: (id) => {
        const explicit = get().visibility[id];
        if (explicit !== undefined) return explicit;
        return LAYER_REGISTRY.find((l) => l.id === id)?.defaultVisible ?? false;
      },
    }),
    {
      name: "mete-layers",
      // Only the user's choices are persisted; anything derived stays in code
      // so a registry change never gets shadowed by a stale localStorage blob.
      partialize: (s) => ({
        visibility: s.visibility,
        year: s.year,
        levelSetting: s.levelSetting,
        transitView: s.transitView,
      }),
    },
  ),
);
